import type { CSSObject } from 'styled-components'

// The palette, fonts, and typography were copied from the theme of the retired DFH React
// components library (0.3.1), which this file used to extend.

const sansSerifFamilies = "'Helvetica', 'Arial', 'sans-serif'"

const fontFamilies = {
  gotham: `'Gotham', ${sansSerifFamilies}`,
  gothamCondensed: `'Gotham Condensed', ${sansSerifFamilies}`,
  gothamNarrow: `'Gotham Narrow', ${sansSerifFamilies}`,
  gothamXNarrow: `'Gotham XNarrow', ${sansSerifFamilies}`,
  roboto: `'Roboto', ${sansSerifFamilies}`,
  robotoCondensed: `'Roboto Condensed', ${sansSerifFamilies}`,
  signature: "'Herr Von Muellerhoff', cursive",
} as const

const brand = {
  white: '#FFFFFF',
  /** rgb(10, 34, 64), PMS 289 C */
  blue: '#0A2240',
  /** primary logo color - rgb(230, 75, 55), PMS 7417 C */
  orange: '#E64B37',
  /** primary logo color - rgb(230, 75, 55), PMS 7417 C */
  red: '#E64B37',
  /** secondary logo color - rgb(84, 86, 90), PMS Cool Gray 11 C */
  gray: '#54565A',
  /** rgb(26, 66, 138), PMS 7687 C */
  categoryBlue: '#1A428A',
  /** rgb(74, 148, 98), PMS 7730 C */
  categoryGreen: '#4A9462',
  /** rgb(224, 132, 50), PMS 7413 C */
  categoryOrange: '#E08432',
  /** rgb(82, 45, 109), PMS 269 C */
  categoryPurple: '#522D6D',
  /** rgb(188, 47, 44), PMS 7620 C */
  categoryRed: '#BC2F2C',
  /** rgb(0, 96, 114), PMS 3155 C */
  categoryTurquoise: '#006072',
  /** rgb(238, 224, 74), PMS 603 C */
  categoryYellow: '#EEE04A',
} as const

type TypographyVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'body1' | 'body2' | 'body3'

const typography: Record<TypographyVariant, CSSObject> = {
  h1: {
    fontFamily: fontFamilies.gotham,
    fontSize: '36px',
    fontWeight: 'bold',
    lineHeight: 1,
    marginTop: 0,
    marginBottom: '0.42em',
  },
  h2: {
    fontFamily: fontFamilies.gotham,
    fontSize: '24px',
    fontWeight: 'bold',
    lineHeight: 1.2,
    marginTop: 0,
    marginBottom: '0.42em',
  },
  h3: {
    fontFamily: fontFamilies.gotham,
    fontSize: '22px',
    fontWeight: 'bold',
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
    marginTop: 0,
    marginBottom: '0.42em',
  },
  h4: {
    fontFamily: fontFamilies.gotham,
    fontSize: '16px',
    fontWeight: 'bold',
    lineHeight: 1,
    marginTop: 0,
    marginBottom: '0.5em',
  },
  h5: {
    fontFamily: fontFamilies.robotoCondensed,
    fontSize: '14px',
    fontWeight: 'bold',
    letterSpacing: '0.06em',
    lineHeight: 1.14,
    marginTop: 0,
    marginBottom: '0.57em',
  },
  body1: {
    fontFamily: fontFamilies.roboto,
    fontSize: '20px',
    lineHeight: 1.7023,
    marginTop: 0,
    marginBottom: '24pt',
  },
  body2: {
    fontFamily: fontFamilies.roboto,
    fontSize: '16px',
    lineHeight: 1.43,
    marginTop: 0,
    marginBottom: '24pt',
  },
  body3: {
    color: '#727477',
    fontFamily: fontFamilies.roboto,
    fontSize: '12px',
    lineHeight: 1.17,
    marginTop: 0,
    marginBottom: '24pt',
  },
}

// add any project specific colors, font families, etc. here
export const extendedTheme = {
  colors: {
    mandyRed: {
      shade100: '#F1B6B9',
      shade200: '#EB9498',
      shade300: '#E47177',
      shade400: '#DE4F56',
      shade500: '#CE272F',
      shade600: '#9F1E24',
      shade700: '#701519',
      shade800: '#410C0F',
      shade900: '#110304',
    },
    poppyRed: {
      shade100: '#E9A3A2',
      shade200: '#E18381',
      shade300: '#DA6260',
      shade400: '#D2423F',
      shade500: '#BC2F2C',
      shade600: '#8F2421',
      shade700: '#611817',
      shade800: '#340D0C',
      shade900: '#060201',
    },
    chestnutRed: {
      shade100: '#F0D3CF',
      shade200: '#E7B6B0',
      shade300: '#DD9990',
      shade400: '#D47C71',
      shade500: '#CA5F52',
      shade600: '#AE4335',
      shade700: '#843228',
      shade800: '#59221B',
      shade900: '#2E110E',
    },
    cinnabarOrange: {
      shade100: '#F8CEC8',
      shade200: '#F4ADA4',
      shade300: '#EF8C80',
      shade400: '#EB6C5B',
      shade500: '#E64B37',
      shade600: '#CB2E19',
      shade700: '#9A2313',
      shade800: '#68170D',
      shade900: '#360C07',
    },
    zestOrange: {
      shade100: '#F5D9C0',
      shade200: '#F0C49C',
      shade300: '#EBAF79',
      shade400: '#E59955',
      shade500: '#E08432',
      shade600: '#BD681D',
      shade700: '#8D4D15',
      shade800: '#5C330E',
      shade900: '#2B1807',
    },
    starYellow: {
      shade100: '#FCFADF',
      shade200: '#F9F3BA',
      shade300: '#F5ED95',
      shade400: '#F2E66F',
      shade500: '#EEE04A',
      shade600: '#E9D717',
      shade700: '#B7A911',
      shade800: '#83790C',
      shade900: '#504A08',
    },
    saladGreen: {
      shade100: '#C7E3D0',
      shade200: '#ACD5B9',
      shade300: '#91C8A3',
      shade400: '#75BA8C',
      shade500: '#5AAD75',
      shade600: '#4a9462',
      shade700: '#376F49',
      shade800: '#254930',
      shade900: '#122418',
    },
    seaGreen: {
      shade100: '#C2EBE5',
      shade200: '#A3E0D8',
      shade300: '#84D6CB',
      shade400: '#66CCBE',
      shade500: '#47C2B1',
      shade600: '#349D8E',
      shade700: '#267368',
      shade800: '#184942',
      shade900: '#0A1E1C',
    },
    stoneBlue: {
      shade100: '#91EEFF',
      shade200: '#68E7FF',
      shade300: '#3FE1FF',
      shade400: '#16DAFF',
      shade500: '#00C7EC',
      shade600: '#00A5C4',
      shade700: '#00829B',
      shade800: '#006072',
      shade900: '#00313A',
    },
    hippieBlue: {
      shade100: '#B9DDE2',
      shade200: '#9CCFD6',
      shade300: '#7FC0CB',
      shade400: '#62B2BF',
      shade500: '#48A2B0',
      shade600: '#387D88',
      shade700: '#275960',
      shade800: '#173439',
      shade900: '#070F11',
    },
    fadedBlue: {
      shade100: '#CDD7DC',
      shade200: '#B5C3CC',
      shade300: '#9CB0BB',
      shade400: '#849CAB',
      shade500: '#6C899A',
      shade600: '#546C7A',
      shade700: '#3D4F59',
      shade800: '#263138',
      shade900: '#0F1416',
    },
    riverBlue: {
      shade100: '#7DAEEB',
      shade200: '#5A98E5',
      shade300: '#3682E0',
      shade400: '#206DCD',
      shade500: '#1B5AAA',
      shade600: '#154787',
      shade700: '#103563',
      shade800: '#0A2240',
      shade900: '#02080F',
    },
    chathamsBlue: {
      shade100: '#AAC3EF',
      shade200: '#88AAE8',
      shade300: '#6592E2',
      shade400: '#4379DC',
      shade500: '#2763CF',
      shade600: '#2052AC',
      shade700: '#1A428A',
      shade800: '#112B5B',
      shade900: '#08152C',
    },
    royalBlue: {
      shade100: '#C1CDF6',
      shade200: '#9DB0F1',
      shade300: '#7993EC',
      shade400: '#5676E7',
      shade500: '#3259E2',
      shade600: '#1B40C1',
      shade700: '#143090',
      shade800: '#0D1F5E',
      shade900: '#060F2D',
    },
    blackPearlBlue: {
      shade100: '#78ACEB',
      shade200: '#5496E5',
      shade300: '#3180E0',
      shade400: '#1E6CCA',
      shade500: '#1959A6',
      shade600: '#144683',
      shade700: '#0E335F',
      shade800: '#09203C',
      shade900: '#02060B',
    },
    darkPurple: {
      shade100: '#CBB0DF',
      shade200: '#B893D3',
      shade300: '#A577C7',
      shade400: '#925ABB',
      shade500: '#7D45A7',
      shade600: '#68398A',
      shade700: '#522D6D',
      shade800: '#341D45',
      shade900: '#160C1E',
    },
    mediumPurple: {
      shade100: '#F2C9F8',
      shade200: '#E9A5F4',
      shade300: '#E080EF',
      shade400: '#D75CEB',
      shade500: '#CE38E6',
      shade600: '#B41ACC',
      shade700: '#88139A',
      shade800: '#5C0D69',
      shade900: '#300737',
    },
    coolGray: {
      cool100: '#f8f9fa',
      cool150: '#f2f3f5',
      cool200: '#eaebee',
      cool250: '#e3e5e9',
      cool300: '#dfe1e5',
      cool400: '#d0d3d8',
      cool500: '#b1b4b9',
      cool600: '#727477',
      cool700: '#4d4f53',
      cool800: '#37393d',
      cool900: '#232427',
      cool450: '#C4C7CC',
      cool550: '#A9B0B5',
    },
    neutralGray: {
      neutral100: '#f9f9f9',
      neutral200: '#ececec',
      neutral300: '#e2e2e2',
      neutral400: '#d4d4d4',
      neutral500: '#b5b5b5',
      neutral600: '#757575',
      neutral700: '#505050',
      neutral800: '#3a3a3a',
      neutral900: '#252525',
    },
    warmGray: {
      warm100: '#faf9f8',
      warm200: '#f0ebe8',
      warm300: '#e7e1dd',
      warm400: '#dbd3cd',
      warm500: '#bfb3ab',
      warm600: '#807269',
      warm700: '#594e47',
      warm800: '#423932',
      warm900: '#2a2420',
    },
    grayscale: {
      white: '#FFFFFF',
      gray5: '#F2F2F2',
      gray10: '#E5E5E5',
      gray20: '#CCCCCC',
      gray40: '#999999',
      gray60: '#666666',
      black: '#000000',
    },
    text: {
      primary: '#232427',
      secondary: '#727477',
    },
    brand,
    component: {
      surfaceBlue: '#F0F5F9',
      textDark: '#051120',
      buttonDisabled: '#C9D6DF',
      checkoutBorderLight: '#D6D6D6',
      backgroundDarkBlue: '#09203C',
      link: brand.red,
      linkVisited: '#CE38E6',
      PrimaryButton: {
        default: {
          background: '#0A2240',
          text: '#FFFFFF',
        },
        hover: {
          background: '#0B2648CC',
        },
        disabled: {
          background: '#d0d3d8',
        },
      },
      SecondaryButton: {
        default: {
          background: '#FFFFFF',
          border: '#0A2240',
          text: '#0A2240',
        },
        hover: {
          background: '#0B2648CC',
          text: '#FFFFFF',
        },
        disabled: {
          background: '#d0d3d8',
        },
      },
    },
    semantic: {
      attention: '#48A2B0',
      error: '#DE4F56',
      featured: '#CA5F52',
      informational: '#6C899A',
      success: '#47C2B1',
    },
    brandBlue: {
      main: brand.blue,
      light100: '#C3CFDE',
      light200: '#DDE3E9',
      light300: '#EDEFF2',
      dark100: '#09203C',
      dark200: '#02162F',
      dark300: '#051120',
    },
    blue: {
      main: brand.blue,
      light100: '#103563',
      light200: '#154787',
      light300: '#1B5AAA',
      light350: '#2763CF',
      light400: '#206DCD',
      light500: '#0084F8',
      dark100: '#02080F',
    },
    brandRed: {
      main: brand.red,
      light100: '#D2423F',
      light200: '#DA6260',
      light300: '#E18381',
      dark050: '#DE4F56',
      dark100: '#8F2421',
      dark200: '#611817',
      dark300: '#340D0C',
    },
    brandGreen: {
      main: brand.categoryGreen,
      light100: '#5AAD75',
      light200: '#75BA8C',
      light300: '#91C8A3',
      dark100: '#376F49',
      dark200: '#254930',
      dark300: '#122418',
    },
    brandYellow: {
      main: brand.categoryYellow,
      light050: '#FFC44D',
      light100: '#F2E66F',
      light200: '#F5ED95',
      light300: '#F9F3BA',
      dark100: '#E9D717',
      dark200: '#B7A911',
      dark300: '#83790C',
    },
  },
  fontFamilies,
  fonts: {
    global: {
      body: fontFamilies.roboto,
      headers: fontFamilies.gotham,
      smallHeaders: fontFamilies.robotoCondensed,
    },
    component: {
      button: fontFamilies.robotoCondensed,
      buttonLink: fontFamilies.roboto,
      dropDownSubtle: fontFamilies.robotoCondensed,
    },
    heading: {
      sizes: [
        {
          // e.g. h5
          fontSize: 14,
        },
        {
          // e.g. h4
          fontSize: 16,
        },
        {
          // e.g. h3
          fontSize: 22,
        },
        {
          // e.g. h2
          fontSize: 24,
        },
        {
          // e.g. h1
          fontSize: 30,
        },
      ],
    },
  },
  typography,
} as const

export type Theme = typeof extendedTheme
